import { Table, Tooltip } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerAvatarPlus } from '../../components/custom_components/AvatarPlus'
import { PerEmpty } from '../../components/custom_components/Empty'
import { MeetingTag } from '../../components/custom_components/MeetingTag'
import { PerTableActionButtons } from '../../components/custom_components/TableActionButtons'
import { tableActionButtons } from '../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import PerText from '../../components/custom_components/Text'
import { useAuth } from '../../context/auth-context'
import { NameDisplayPreference } from '../../graphql/enum/name-display-preference.enum'
import useDeclineMeetingRequest from '../../graphql/meeting/hooks/useDeclineMeetingRequest'
import { computeFullname } from '../../shared/nameDisplayPreferencesUtil'
import { useDateTime } from '../../shared/useDateTime'
import { MeetingRequestDto } from '../../types'
import { RequestMeetingDeclineModal } from './cancel/RequestMeetingDeclineModal'
import { MeetingInviteModal } from './invite/MeetingInviteModal'
import { showNotification } from '../../components/custom_components/Notification/notification'

type RequestedMeetingsTableProps = {
  requestedMeetings: MeetingRequestDto[]
}
export const RequestedMeetingsTable: FC<RequestedMeetingsTableProps> = ({ requestedMeetings }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [acceptModalVisible, setAcceptModalVisible] = useState<boolean>(false)
  const [declineModalVisible, setDeclineModalVisible] = useState<boolean>(false)
  const [modalState, setModalState] = useState<MeetingRequestDto>(requestedMeetings[0])
  const { compareDates, formatDateShort } = useDateTime(user)
  const { tableActionButtonsRowWidth } = tableActionButtons()
  const { declineMeetingRequest } = useDeclineMeetingRequest()

  const onDeclineRequest = async (requestMeetingId: number, reasonOfRejection: string, reviewerId: number): Promise<string | undefined> => {
    setDeclineModalVisible(false)
    const { errors } = await declineMeetingRequest(requestMeetingId, reasonOfRejection, reviewerId)
    if (!!errors && !!errors.length) {
      return t('global.notifications.error.description')
    } else {
      showNotification({
        type: 'success',
        message: t(`meetingView.requestedMeetings.modal.notifications.success.title`),
        description: t(`meetingView.requestedMeetings.modal.notifications.success.description`, {
          requesterFirstName: modalState.requester.firstName,
        }),
      })
      return undefined
    }
  }

  const employeeTableHeader = (
    <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('meetingView.requestedMeetings.table.employee')}</Tooltip>
  )

  const columns = [
    {
      key: 'requestedOn',
      title: t('meetingView.requestedMeetings.table.requestedOn'),
      className: 'minWidth-column-m',
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (meetingA: MeetingRequestDto, meetingB: MeetingRequestDto) => {
        return compareDates(meetingA.requestedDate, meetingB.requestedDate)
      },
      render: (meetingRequest: MeetingRequestDto) => <PerText>{formatDateShort(meetingRequest.requestedDate)}</PerText>,
    },
    {
      key: 'appraisalSeason',
      title: t('meetingView.requestedMeetings.table.appraisalSeason'),
      className: 'minWidth-column-s',
      sorter: (meetingA: MeetingRequestDto, meetingB: MeetingRequestDto) => {
        const comparison = meetingA.appraisalSeason.year - meetingB.appraisalSeason.year
        return comparison
      },
      render: (meetingRequest: MeetingRequestDto) => <PerText>{meetingRequest.appraisalSeason.year}</PerText>,
    },
    {
      key: 'type',
      title: t('meetingView.requestedMeetings.table.type'),
      className: 'minWidth-column-m',
      render: (meetingRequest: MeetingRequestDto) => <MeetingTag type={meetingRequest.type} />,
    },
    {
      key: 'employee',
      className: 'minWidth-column-xl',
      title: employeeTableHeader,
      sorter: (rowDataA: MeetingRequestDto, rowDataB: MeetingRequestDto) => {
        const nameA = computeFullname(user.nameDisplayPreference, rowDataA.requester.firstName, rowDataA.requester.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, rowDataB.requester.firstName, rowDataB.requester.lastName)
        return nameA.localeCompare(nameB)
      },
      render: (meetingRequest: MeetingRequestDto) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={meetingRequest.requester.id}
          firstName={meetingRequest.requester.firstName}
          lastName={meetingRequest.requester.lastName}
          infoText={''}
          data-e2e-meeting-table-avatar
        />
      ),
    },
    {
      key: 'reason',
      className: 'minWidth-column-xl',
      title: t('meetingView.requestedMeetings.table.reason'),
      render: (meetingRequest: MeetingRequestDto) => (
        <PerText className="ellipsis--line-3" appearance="flowing-text" color="secondary">
          {meetingRequest.reasonForRequest}
        </PerText>
      ),
    },
    {
      key: 'actions',
      title: t('table.actions'),
      className: 'textAlign-left',
      width: tableActionButtonsRowWidth(2),
      render: (meetingRequest: MeetingRequestDto) => (
        <PerTableActionButtons
          variant="pastel"
          justify="center"
          buttons={[
            {
              icon: 'check_stroke',
              color: 'success',
              disabled: !meetingRequest.isRequestValid,
              tooltip: !meetingRequest.isRequestValid
                ? t('meetingView.requestedMeetings.button.invalidRequest')
                : t('global.button.accept'),
              onClick: () => {
                setAcceptModalVisible(true)
                setModalState(meetingRequest)
              },
            },
            {
              icon: 'close_stroke',
              color: 'error',
              tooltip: t('global.button.decline'),
              onClick: () => {
                setDeclineModalVisible(true)
                setModalState(meetingRequest)
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      {acceptModalVisible && (
        <MeetingInviteModal
          visible={acceptModalVisible}
          onCancel={() => setAcceptModalVisible(false)}
          employeeList={[modalState.requester]}
          requestedMeeting={modalState}
        />
      )}
      {declineModalVisible && (
        <RequestMeetingDeclineModal
          isOpen={declineModalVisible}
          onClose={() => setDeclineModalVisible(false)}
          onDeclineRequest={async (requestMeetingId, reasonOfRejection, reviewerId) =>
            onDeclineRequest(requestMeetingId, reasonOfRejection, reviewerId)
          }
          requestedMeeting={modalState}
        />
      )}
      <Table
        className="light small viewWithTabsAndInfoBox"
        dataSource={requestedMeetings.map((o) => ({ ...o, key: o.id }))}
        columns={columns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: requestedMeetings.length === 0 && (
            <PerEmpty icon="chatNo_stroke" heading={t('meetingView.requestedMeetings.table.empty')} />
          ),
        }}
        data-e2e-manager-meetings-table
      />
    </>
  )
}
